import AbandonedCart from "@/assets/icons/AbandonedCart";
import Loader from "@/components/Loader/Loader";
import Divider from "@/components/shared/Divider/Divider";
import { ExportButton } from "@/components/shared/ExportButton";
import { MultiComboBox } from "@/components/shared/MultiComboBox/MultiComboBox";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import LayoutInside from "@/layout/LayoutInside";
import DateRangePicker from "@/pages/Dashboard/Partials/DateRangePicker";
import WhatsIcon from "@/assets/icons/WhatsIcon";
import { CheckoutsService } from "@/services/checkouts.service";
import { ProductsService } from "@/services/products.service";
import { ReportsService } from "@/services/reports.service";
import { IS_PRODUCTION } from '@/utils/constants';
import {
  checkoutTemplateLabel,
  fetchReportTemplateOptions,
} from '@/utils/checkoutTemplates';
import { useQuery } from '@tanstack/react-query';
import { countries } from 'country-data-list';
import { format } from 'date-fns';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function CartAbandonment() {
  const { t } = useTranslation(['reports', 'common']);
  const [selectedProductId, setSelectedProductId] = useState<string[] | null>(
    null
  );
  const [selectedCheckout, setSelectedCheckout] = useState<string[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date(),
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProductId, selectedCheckout, selectedTemplates, dateRange]);

  const { data: sales, isLoading: isLoadingSales } = useQuery({
    queryKey: [
      'cart-abandonment',
      selectedProductId,
      selectedCheckout,
      selectedTemplates,
      dateRange,
      currentPage,
    ],
    queryFn: async () =>
      await ReportsService.getReportsCartAbandoned({
        startDate: format(dateRange?.from || new Date(), 'yyyy-MM-dd'),
        endDate: format(dateRange?.to || new Date(), 'yyyy-MM-dd'),
        filterParams: {
          checkouts: selectedCheckout,
          products: selectedProductId,
          templates: selectedTemplates,
        },
        page: currentPage,
      }),
  });

  const headers = [
    { title: t('reports:cart_abandonment.table.datetime'), key: 'datetime' },
    { title: t('reports:cart_abandonment.table.product'), key: 'product' },
    { title: t('reports:template'), key: 'template' },
    { title: t('reports:cart_abandonment.table.price'), key: 'price' },
    { title: t('reports:cart_abandonment.table.name'), key: 'name' },
    { title: t('reports:cart_abandonment.table.email'), key: 'email' },
    { title: t('reports:cart_abandonment.table.phone'), key: 'phone' },
    { title: t('reports:cart_abandonment.table.country'), key: 'country' },
    { title: t('reports:cart_abandonment.table.whatsapp'), key: 'whatsapp' },
    {
      title: t('reports:cart_abandonment.table.checkout_link'),
      key: 'checkoutLink',
    },
  ];

  const fetchOptions = async (query?: string) => {
    return await ProductsService.getAllProductsBasicOptions(query);
  };

  const fetchCheckoutsOptions = async (query?: string) => {
    if (!selectedProductId || selectedProductId?.length === 0) return [];

    const checkouts = await CheckoutsService.getCheckoutsByProducts({
      productIds: selectedProductId,
      query,
    });

    return checkouts;
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  useEffect(() => {
    const updateSelectedCheckouts = async () => {
      if (!selectedProductId?.length) {
        setSelectedCheckout([]);
        return;
      }

      const availableCheckouts = await fetchCheckoutsOptions();
      const availableCheckoutIds = availableCheckouts.map((c) => c.value);

      setSelectedCheckout((prevSelected) =>
        prevSelected.filter((checkoutId) =>
          availableCheckoutIds.includes(checkoutId)
        )
      );
    };

    updateSelectedCheckouts();
  }, [selectedProductId]);

  return (
    <LayoutInside
      items={[
        { text: t('common:reports'), link: '/reports' },
        {
          text: t('reports:cart_abandonment.title'),
          link: '/reports/cart-abandonment',
        },
      ]}
    >
      {false ? (
        <div className='flex items-center justify-center h-[calc(100vh-120px)]'>
          <Loader />
          <span className='sr-only'>{t('common:loading')}</span>
        </div>
      ) : (
        <>
          <Link to='/reports' className='w-fit'>
            <Button
              variant='default'
              className='bg-transparent text-colorText hover:bg-transparent p-0'
            >
              <ArrowLeft size={16} />
              {t('common:back')}
            </Button>
          </Link>

          <section className='my-6'>
            <h1 className='text-xl font-semibold'>
              {t('reports:cart_abandonment.title')}
            </h1>
            <p className='text-sm text-muted-foreground mt-1'>
              {t('reports:cart_abandonment.description')}
            </p>
          </section>

          <Card className='w-full mt-12 overflow-x-auto'>
            <div className='flex md:items-center flex-col md:flex-row w-full px-4 py-6 justify-between max-md:gap-3'>
              <h1 className='text-base font-semibold'>
                {t('reports:cart_abandonment.title')}
              </h1>
            </div>

            <Divider />

            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 p-4 gap-4'>
              <DateRangePicker
                onChange={handleDateRangeChange}
                className='!w-full'
                widthFull
              />
              <MultiComboBox
                fetchOptions={fetchOptions}
                values={selectedProductId}
                onChange={setSelectedProductId}
                placeholder={t('common:products_placeholder')}
                queryKey='products'
              />

              <MultiComboBox
                fetchOptions={fetchCheckoutsOptions}
                values={selectedCheckout}
                onChange={setSelectedCheckout}
                placeholder={t('common:select_checkout')}
                queryKey='checkouts'
                newParams={selectedProductId}
                disabled={!selectedProductId?.length}
              />

              <MultiComboBox
                fetchOptions={fetchReportTemplateOptions}
                values={selectedTemplates}
                onChange={setSelectedTemplates}
                placeholder={t('reports:select_template')}
                queryKey='report-templates-cart-abandonment'
              />

              <ExportButton
                type="abandoned-carts"
                filters={{
                  startDate: format(dateRange?.from || new Date(), 'yyyy-MM-dd'),
                  endDate: format(dateRange?.to || new Date(), 'yyyy-MM-dd'),
                  filterParams: {
                    products: selectedProductId || undefined,
                    checkouts: selectedCheckout?.length > 0 ? selectedCheckout : undefined,
                    templates: selectedTemplates.length > 0 ? selectedTemplates : undefined,
                  },
                }}
                label={t('sales:export')}
                title={t("sales:export_modal.title")}
                variant="secondary"
                size="sm"
              />
            </div>

            <TableListCustom
              headers={headers}
              isLoadingData={isLoadingSales}
              data={sales}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              emptyTitleMessage={t('reports:cart_abandonment.empty.title')}
              emptyDescriptionMessage={t(
                'reports:cart_abandonment.empty.description'
              )}
              emptyIconMessage={<AbandonedCart />}
            >
              {sales?.data?.map((item, index) => {
                const phonePrefix = item?.customer?.phone_prefix || '55';
                const phoneNumber =
                  item?.customer?.phone?.replace(/\D/g, '') || '';

                const fullPhoneNumber = phonePrefix + phoneNumber;

                const allCountries = countries.all;

                const countryByPhonePrefix = allCountries.find((country) =>
                  country.countryCallingCodes.includes(`+${phonePrefix}`)
                );

                const countryByAlpha2 = item?.country?.code
                  ? allCountries.find(
                    (country) => country.alpha2 === item.country.code
                  )
                  : undefined;

                const countryEmoji =
                  countryByAlpha2?.emoji || countryByPhonePrefix?.emoji || '🇧🇷';
                const countryName =
                  countryByAlpha2?.name ||
                  countryByPhonePrefix?.name ||
                  'Brasil';

                const country = `${countryEmoji} ${countryName}`;

                return (
                  <TableRow
                    key={index}
                    data-cy='table-row'
                    className='h-[64px] border-b border-b-colorBorder hover:bg-transparent'
                  >
                    <TableCell>
                      {item?.updated_at_formatted ||
                        t('common:information_not_available')}
                    </TableCell>
                    <TableCell>
                      {item?.product?.name ||
                        t('common:information_not_available')}
                    </TableCell>
                    <TableCell>{checkoutTemplateLabel(item?.template)}</TableCell>
                    <TableCell>
                      {item?.price_formatted ||
                        t('common:information_not_available')}
                    </TableCell>
                    <TableCell>
                      {item?.customer?.name ||
                        t('common:information_not_available')}
                    </TableCell>
                    <TableCell>
                      {item?.customer?.email ||
                        t('common:information_not_available')}
                    </TableCell>
                    <TableCell>
                      {item?.customer?.phone
                        ? fullPhoneNumber
                        : t('common:information_not_available')}
                    </TableCell>
                    <TableCell>{country}</TableCell>
                    <TableCell>
                      <Button
                        variant='outline'
                        size='sm'
                        asChild
                        onClick={() => {
                          const message = ``;
                          const whatsappLink = `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(
                            message
                          )}`;
                          window.open(whatsappLink, '_blank');
                        }}
                      >
                        <div>
                          <WhatsIcon />
                          {t('reports:cart_abandonment.table.whatsapp')}
                        </div>
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          const linkToCheckout = `${IS_PRODUCTION
                              ? 'https://pay.mundpay.com'
                              : 'https://go.paybesty.com'
                            }/${item?.checkout.id}?ref=&name=${encodeURIComponent(
                              item?.customer?.name
                            )}&email=${encodeURIComponent(
                              item?.customer?.email
                            )}&phone=${encodeURIComponent(
                              item?.customer?.phone.replace(/\D/g, '') || ''
                            )}`;
                          window.open(linkToCheckout, '_blank');
                        }}
                      >
                        <div className='flex gap-2'>
                          <ExternalLink className='w-4 h-4 mr-1' />
                          {t('reports:cart_abandonment.table.checkout_link')}
                        </div>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableListCustom>
          </Card>
        </>
      )}
    </LayoutInside>
  );
}
